import { useQuery } from '@tanstack/react-query';
import { fetchDepartures, pairsAtSite, type Pair } from './sl.ts';
import { retryDelay, shouldRetry } from './retry.ts';
import { refreshIntervalMs } from './activity.ts';
import { loadDepartures, saveDepartures } from './cache.ts';
import { loadSites, nearbySites, searchSites } from './sites.ts';

/**
 * `expect` is the stop's saved lines. It is not part of the query key --
 * changing which lines you track does not invalidate the departures already
 * fetched for that stop -- it is only passed along so a short answer missing
 * one of them can be spotted and asked for again.
 */
export function useDepartures(siteId: number, expect: Pair[] = [], enabled = true) {
  return useQuery({
    queryKey: ['departures', siteId],
    queryFn: async ({ signal }) => {
      const result = await fetchDepartures(siteId, { expect, signal });
      saveDepartures(siteId, result, expect);
      return result;
    },
    // What we knew last time, so the board opens with times rather than with
    // nothing while SL is refusing. `initialDataUpdatedAt` is what makes this
    // honest: the query treats the data as being of that age, so it refetches
    // at once and the stop's header can say how old what you are looking at
    // is. Read lazily -- this runs only when the query has no data yet.
    initialData: () => loadDepartures(siteId)?.response,
    initialDataUpdatedAt: () => loadDepartures(siteId)?.fetchedAt,
    // A collapsed stop is one you have said you do not want to look at, so it
    // stops polling too -- otherwise a board of stops you have tidied away
    // keeps asking SL about all of them every thirty seconds.
    enabled,
    // Departure times are the one thing in this app that is never worth
    // reusing from cache: a minute-old board is a wrong board.
    staleTime: 0,
    // A function, so it is recomputed each time the query settles and can
    // follow attention rather than being one fixed number. See activity.ts.
    refetchInterval: () => refreshIntervalMs(),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: shouldRetry,
    retryDelay,
  });
}

/**
 * The line + direction pairs a stop offers, for the setup picker.
 *
 * A 20-hour window rather than the board's one hour: this needs the stop's
 * whole repertoire, not what is arriving now, or setting up in the evening
 * would offer you a fraction of the lines.
 */
export function usePairsAtSite(siteId: number | null) {
  return useQuery({
    queryKey: ['pairs', siteId],
    queryFn: async ({ signal }) => {
      const { departures } = await fetchDepartures(siteId as number, {
        forecast: 1200,
        signal,
      });
      return pairsAtSite(departures);
    },
    enabled: siteId !== null,
    // A stop's set of lines changes with the timetable, not with the minute.
    staleTime: 60 * 60_000,
    retry: shouldRetry,
    retryDelay,
  });
}

/**
 * Stop search. The snapshot is fetched once and kept for the session; Infinity
 * staleness is honest here, because the file cannot change without a redeploy.
 */
export function useSiteSearch(query: string) {
  const sites = useQuery({
    queryKey: ['sites'],
    queryFn: loadSites,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    ...sites,
    results: sites.data ? searchSites(sites.data, query) : [],
  };
}

/**
 * One stop's record from the snapshot, for screens reached by URL rather than
 * by tapping a search result -- a bookmarked editor, or a reload.
 */
export function useSite(siteId: number | null) {
  return useQuery({
    queryKey: ['sites'],
    queryFn: loadSites,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: siteId !== null,
    select: (sites) => sites.find((s) => s.id === siteId) ?? null,
  });
}

/**
 * Stops within walking distance of this one, for when a line you expected is
 * not on the list because it actually serves the site next door.
 */
export function useNearby(siteId: number | null) {
  return useQuery({
    queryKey: ['sites'],
    queryFn: loadSites,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: siteId !== null,
    select: (sites) => {
      const origin = sites.find((s) => s.id === siteId);
      return origin ? nearbySites(sites, origin) : [];
    },
  });
}
